import { useEffect, useRef, useState } from "react";
import { useCountUp } from "../lib/useReveal";
import { api } from "../lib/api";
import { HandHeart, Sprout, Bird, Globe2 } from "lucide-react";

function Stat({ target, label, Icon, suffix = "" }) {
  const wrap = useRef(null);
  const [start, setStart] = useState(false);
  const numRef = useCountUp(target, start, 1800);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { setStart(true); io.unobserve(el); }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrap}
      className="text-center py-6 md:py-8 px-2 sm:px-4 border-r border-white/25 [&:nth-child(2n)]:border-r-0 md:[&:nth-child(2n)]:border-r md:last:border-r-0 min-w-0"
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex justify-center mb-3 md:mb-4">
        <Icon className="w-12 h-12 md:w-20 md:h-20 text-white" strokeWidth={1.3} />
      </div>
      <div className="stat-num text-4xl sm:text-5xl md:text-6xl text-white leading-none">
        <span ref={numRef}>0</span>
        <span>{suffix}</span>
      </div>
      <div className="mt-3 text-white/90 font-display font-medium text-sm md:text-base">
        {label}
      </div>
    </div>
  );
}

export default function Impact() {
  const [stats, setStats] = useState({
    years_of_work: 6,
    districts_served: 4,
    schools_adopted: 3,
    visionaries_joined: 32,
    visionaries_target: 100,
  });

  useEffect(() => {
    api.get("/impact/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <section id="impact" data-testid="impact-section" className="relative bg-teal text-white overflow-hidden">
      {/* Subtle background icon pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden>
        <div className="absolute -left-8 top-1/2 -translate-y-1/2">
          <HandHeart className="w-[420px] h-[420px]" strokeWidth={0.6} />
        </div>
        <div className="absolute -right-8 top-1/2 -translate-y-1/2">
          <Globe2 className="w-[420px] h-[420px]" strokeWidth={0.6} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10 md:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4">
          <Stat target={stats.years_of_work} label="Years of Work" suffix="+" Icon={HandHeart} />
          <Stat target={stats.visionaries_joined} label={`Visionaries (of ${stats.visionaries_target})`} Icon={Sprout} />
          <Stat target={stats.schools_adopted} label="Schools Adopted" Icon={Bird} />
          <Stat target={stats.districts_served} label="Districts Served" Icon={Globe2} />
        </div>
      </div>
    </section>
  );
}
